import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaFolder, FaTimes, FaArrowLeft, FaSearch } from "react-icons/fa";
import ENDPOINTS from "../../api/endpoint";
import { showErrorToast } from "../Toast";

function normalizeFoldersResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.folders)) return data.folders;
  if (Array.isArray(data.items)) {
    return data.items.filter(
      (x) =>
        x?.type === "folder" ||
        x?.isFolder === true ||
        x?.folder === true ||
        x?.mimeType === "application/vnd.google-apps.folder"
    );
  }
  if (Array.isArray(data.value)) {
    return data.value.filter((x) => x?.folder || x?.type === "folder");
  }
  return [];
}

/**
 * Folder picker for auto ingestion.
 * - google_drive / onedrive: browse folders from the drive root.
 * - sharepoint: pick a site, then a document library, then browse folders inside it.
 *   onSelectFolder receives { id, name, driveId } (driveId only for SharePoint).
 * Everything beneath the selected folder (all subfolders) is ingested by the backend.
 */
const DriveFolderPicker = ({
  isOpen,
  onClose,
  driveType, // "google_drive" | "onedrive" | "sharepoint"
  companyId,
  userId,
  title,
  onSelectFolder,
}) => {
  const isSharePoint = driveType === "sharepoint";

  const [current, setCurrent] = useState({ id: "root", name: "Root" });
  const [history, setHistory] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);

  // SharePoint-only navigation state
  const [stage, setStage] = useState("folders"); // "sites" | "libraries" | "folders"
  const [siteQuery, setSiteQuery] = useState("");
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [library, setLibrary] = useState(null);

  const computedTitle = useMemo(() => {
    if (title) return title;
    if (driveType === "sharepoint") return "Select SharePoint folder";
    return driveType === "onedrive"
      ? "Select OneDrive folder"
      : "Select Google Drive folder";
  }, [title, driveType]);

  const authHeaders = useMemo(
    () => ({ "Content-Type": "application/json", "X-User-Id": userId }),
    [userId]
  );

  const fetchJson = useCallback(
    async (url, failMsg) => {
      const res = await fetch(url, { method: "GET", headers: authHeaders });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showErrorToast(err.detail || err.message || failMsg);
        return null;
      }
      return res.json();
    },
    [authHeaders]
  );

  const fetchFolders = useCallback(
    async (folderId, driveId) => {
      if (!companyId || !userId) return;
      setLoading(true);
      try {
        const url = isSharePoint
          ? ENDPOINTS.GET_SHAREPOINT_FOLDERS(companyId, driveId, folderId)
          : ENDPOINTS.GET_DRIVE_FOLDERS(companyId, driveType, folderId);
        const data = await fetchJson(url, "Failed to load folders");
        if (!data) {
          setFolders([]);
          return;
        }
        const list = normalizeFoldersResponse(data);
        setFolders(
          list.map((f) => ({
            id: f.id,
            name: f.name,
            webViewLink: f.webViewLink || f.webUrl || null,
          }))
        );
      } catch (e) {
        console.error("Folder list error:", e);
        showErrorToast("Failed to load folders");
        setFolders([]);
      } finally {
        setLoading(false);
      }
    },
    [companyId, userId, driveType, isSharePoint, fetchJson]
  );

  const loadSites = useCallback(
    async (q) => {
      if (!companyId || !userId) return;
      setLoading(true);
      try {
        const data = await fetchJson(
          ENDPOINTS.GET_SHAREPOINT_SITES(companyId, q),
          "Failed to load SharePoint sites"
        );
        setSites(data?.sites || []);
      } catch (e) {
        console.error("SharePoint sites error:", e);
        showErrorToast("Failed to load SharePoint sites");
        setSites([]);
      } finally {
        setLoading(false);
      }
    },
    [companyId, userId, fetchJson]
  );

  const loadLibraries = useCallback(
    async (siteId) => {
      setLoading(true);
      try {
        const data = await fetchJson(
          ENDPOINTS.GET_SHAREPOINT_LIBRARIES(companyId, siteId),
          "Failed to load document libraries"
        );
        setLibraries(data?.libraries || []);
      } catch (e) {
        console.error("SharePoint libraries error:", e);
        showErrorToast("Failed to load document libraries");
        setLibraries([]);
      } finally {
        setLoading(false);
      }
    },
    [companyId, fetchJson]
  );

  useEffect(() => {
    if (!isOpen) return;
    setHistory([]);
    setFolders([]);
    if (isSharePoint) {
      setStage("sites");
      setSiteQuery("");
      setSite(null);
      setLibrary(null);
      setSites([]);
      setLibraries([]);
      setCurrent({ id: "root", name: "Root" });
      loadSites("");
    } else {
      setStage("folders");
      setCurrent({ id: "root", name: "Root" });
      fetchFolders("root");
    }
  }, [isOpen, isSharePoint, fetchFolders, loadSites]);

  const handleBack = () => {
    if (isSharePoint) {
      if (stage === "folders" && history.length === 0) {
        setStage("libraries");
        setLibrary(null);
        return;
      }
      if (stage === "libraries") {
        setStage("sites");
        setSite(null);
        return;
      }
    }
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setCurrent(prev);
    fetchFolders(prev.id, library?.id);
  };

  const handleOpen = (folder) => {
    setHistory((h) => [...h, current]);
    setCurrent({ id: folder.id, name: folder.name });
    fetchFolders(folder.id, library?.id);
  };

  const handleSelect = (folder) => {
    if (isSharePoint) {
      // Readable name: Site / Library / sub / folder
      const trail = history.slice(1).map((h) => h.name);
      const parts = [site?.name, library?.name, ...trail];
      if (current.id !== "root") parts.push(current.name);
      parts.push(folder.name);
      onSelectFolder?.({
        id: folder.id,
        name: parts.filter(Boolean).join(" / "),
        driveId: library?.id,
      });
    } else {
      onSelectFolder?.(folder);
    }
    onClose?.();
  };

  const handleSelectLibraryRoot = () => {
    onSelectFolder?.({
      id: "root",
      name: [site?.name, library?.name].filter(Boolean).join(" / "),
      driveId: library?.id,
    });
    onClose?.();
  };

  const handlePickSite = (s) => {
    setSite(s);
    setStage("libraries");
    loadLibraries(s.id);
  };

  const handlePickLibrary = (lib) => {
    setLibrary(lib);
    setStage("folders");
    setHistory([]);
    setCurrent({ id: "root", name: lib.name });
    fetchFolders("root", lib.id);
  };

  if (!isOpen) return null;

  const canGoBack =
    isSharePoint
      ? stage === "libraries" || stage === "folders"
      : history.length > 0;

  const rowClass =
    "w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors";

  const renderPickList = (items, onPick, emptyText, label) =>
    items.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <FaFolder className="w-14 h-14 mb-2 opacity-50" />
        <p>{emptyText}</p>
      </div>
    ) : (
      <div className="space-y-2">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => onPick(it)}
            className={`${rowClass} text-left`}
          >
            <FaFolder className="w-7 h-7 text-amber-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-800 dark:text-white truncate">
                {it.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {it.webUrl || label}
              </p>
            </div>
          </button>
        ))}
      </div>
    );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
            {computedTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
            aria-label="Close"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Path */}
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
          {canGoBack && (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors"
            >
              <FaArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 min-w-0">
            <FaFolder className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="font-medium truncate">
              {isSharePoint && stage === "sites"
                ? "SharePoint sites"
                : isSharePoint && stage === "libraries"
                ? `${site?.name || "Site"} – document libraries`
                : isSharePoint
                ? `${site?.name || "Site"} / ${library?.name || ""}${
                    current.id !== "root" ? ` / ${current.name}` : ""
                  }`
                : current.name}
            </span>
          </div>
        </div>

        {/* SharePoint site search */}
        {isSharePoint && stage === "sites" && (
          <form
            className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-600 flex-shrink-0"
            onSubmit={(e) => {
              e.preventDefault();
              loadSites(siteQuery.trim());
            }}
          >
            <input
              type="text"
              value={siteQuery}
              onChange={(e) => setSiteQuery(e.target.value)}
              placeholder="Search sites, or paste a SharePoint site URL"
              className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            />
            <button
              type="submit"
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              <FaSearch className="w-3.5 h-3.5" />
              Search
            </button>
          </form>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          ) : isSharePoint && stage === "sites" ? (
            renderPickList(sites, handlePickSite, "No sites found", "SharePoint site")
          ) : isSharePoint && stage === "libraries" ? (
            renderPickList(
              libraries,
              handlePickLibrary,
              "No document libraries found",
              "Document library"
            )
          ) : (
            <div className="space-y-2">
              {isSharePoint && current.id === "root" && (
                <div className={rowClass}>
                  <FaFolder className="w-7 h-7 text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 dark:text-white truncate">
                      Entire library
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Every folder in {library?.name}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectLibraryRoot}
                    className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Select
                  </button>
                </div>
              )}
              {folders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                  <FaFolder className="w-14 h-14 mb-2 opacity-50" />
                  <p>No folders found</p>
                </div>
              ) : (
                folders.map((folder) => (
                  <div key={folder.id} className={rowClass}>
                    <button
                      type="button"
                      onClick={() => handleOpen(folder)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <FaFolder className="w-7 h-7 text-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 dark:text-white truncate">
                          {folder.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Folder
                        </p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelect(folder)}
                      className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Select
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {driveType === "google_drive"
              ? "Select a folder to enable auto ingestion."
              : "Select a folder to enable auto ingestion. All of its subfolders are included."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DriveFolderPicker;
